import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { ErrorText } from "@/components/ui/error";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { loginPath, shopify_login_auth_route, VITE_API_URL } from "@/constants";
import { Info } from "lucide-react";
import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/auth/shopify/login")({
  component: RouteComponent,
});

// example provided by shopify
function isValidShopDomain(shop: string) {
  return /^[a-zA-Z0-9][a-zA-Z0-9\-]*\.myshopify\.com$/.test(shop);
}

function RouteComponent() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const [shop, setShop] = useState("");
  const [err, setErr] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);

    if (!isValidShopDomain(shop)) {
      setErr(t("auth.shopifyLogin.enterValidDomain"));
      setIsSubmitting(false);
      return;
    }

    try {
      const resp = await fetch(`${VITE_API_URL}${shopify_login_auth_route}`, {
        method: "POST",
        headers: {
          "Content-type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          shop: shop,
        }),
      });
      if (!resp.ok) {
        throw new Error("failed to authorize");
      }

      const data = (await resp.json()) as { url: string };
      nav({
        href: data.url,
        reloadDocument: true,
      });
    } catch (e) {
      console.error(e);
      setErr(t("auth.shopifyLogin.invalidDomain"));
    }
    setIsSubmitting(false);
  };

  return (
    <div className="flex h-screen items-center justify-center">
      <div className="w-full flex flex-col space-y-3">
        <div className="mx-auto w-full max-w-80 relative bottom-8 flex flex-col gap-6">
          <div className="flex w-full flex-col items-center gap-1">
            <h1 className="w-full text-left text-2xl font-semibold tracking-tight text-primary">
              {t("auth.shopifyLogin.title")}
            </h1>
            <div className="flex space-x-2 w-full items-center">
              <p className="text-left text-sm text-gray-500">
                {t("auth.shopifyLogin.subtitle")}
              </p>

              <Tooltip>
                <TooltipTrigger>
                  <Info className="size-4 text-gray-500" />
                </TooltipTrigger>
                <TooltipContent side="right">
                  <p>{t("auth.shopifyLogin.billingShop")}</p>
                </TooltipContent>
              </Tooltip>
            </div>
          </div>

          <form method="post" onSubmit={handleSubmit} className="space-y-4">
            {err !== "" && (
              <ErrorText className="w-full text-center">{err}</ErrorText>
            )}
            <div className="space-y-2">
              <Label htmlFor="shop" className="text-gray-500">
                {t("auth.shopifyLogin.shopDomain")}
              </Label>
              <Input
                type="text"
                name="shop"
                onChange={(e) => {
                  setShop(e.currentTarget.value);
                  setErr("");
                }}
                placeholder="my-store.myshopify.com"
                className="h-10 rounded-sm w-full text-sm font-normal placeholder:text-gray-400"
                required
              />
            </div>

            <Button
              type="submit"
              disabled={isSubmitting}
              className="flex h-9 items-center justify-center text-white w-full rounded-sm"
            >
              {isSubmitting ? t("common.sending") : t("common.continue")}
            </Button>
            <div className="w-full text-xs font-medium text-slate-600">
              <span>
                {t("auth.shopifyLogin.useEmail")}{" "}
                <Link className="text-primary" to={loginPath}>
                  {t("auth.shopifyLogin.loginWithEmail")}
                </Link>
              </span>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
